import React, { useCallback, useRef, useState } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  NumberInput,
  Button,
  Group,
  Stack,
  Select,
  Box,
  ActionIcon,
  Checkbox,
  Tabs,
  Table,
  Loader,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { ApiService } from '../../api/api-service';
import { Props, PredefinedService, CustomService, Student } from '../types/package.types';
import { showNotification } from '@mantine/notifications';
import EnrollmentAttachments, { uploadEnrollmentAttachments } from './EnrollmentAttachments';

const PREDEFINED_SERVICES = [
  { value: 'training', label: 'Training' },
  { value: 'resume', label: 'Resume' },
  { value: 'linkedIn', label: 'LinkedIn' },
  { value: 'mockInterview', label: 'Mock Interview' },
  { value: 'certification', label: 'Certification' },
  { value: 'liveProject', label: 'Live Project' },
];
// 
const PACKAGE_GENERAL_STATUS_OPTIONS = [
  { value: 'Not Started', label: 'Not Started' },
  { value: 'In-Progress', label: 'In-Progress' },
  { value: 'On-Hold', label: 'On-Hold' },
  { value: 'Payment Issue', label: 'Payment Issue' },
  { value: 'Discard', label: 'Discard' },
  { value: 'Completed', label: 'Completed' },
];
const PACKAGE_STATUS_OPTIONS = [
  { value: 'Pending', label: 'Pending' },
  { value: 'In-progress', label: 'In-progress' },
  { value: 'Hold', label: 'Hold' },
  { value: 'Completed', label: 'Completed' },
];

interface Payment {
  date: string;
  amount: number;
  addedAt?: string;
  currency?: Currency;
}

export type Currency = 'USD' | 'INR' | 'CAD' | 'GBP' | 'NAIRA' | 'EURO' | 'AUD' | 'AED';

const CURRENCY_OPTIONS = [
  { value: 'INR', label: '₹ INR' },
  { value: 'USD', label: '$ USD' },
  { value: 'CAD', label: '$ CAD' },
  { value: 'GBP', label: '£ GBP' },
  { value: 'NAIRA', label: '₦ NAIRA' },
  { value: 'EURO', label: '€ EURO' },
  { value: 'AUD', label: '$ AUD' },
  { value: 'AED', label: 'د.إ AED' }
];


export default function CreatePackageModal({ opened, onClose, onCreated, initialData }: Props) {
  const [packageId, setPackageId] = useState('');
  const [packageType, setPackageType] = useState<string | null>(null);
  const [packageGeneralStatus, setPackageGeneralStatus] = useState<string>('Not Started'); 
  const [batchNumber, setBatchNumber] = useState('');
  const [studentName, setStudentName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [trainerEmail, setTrainerEmail] = useState('');
  const [description, setDescription] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
  
  // Main package financial fields
  const [courseName, setCourseName] = useState('');
  const [price, setPrice] = useState<number>(0);
  const [receivedAmount, setReceivedAmount] = useState<number>(0);
  const [remainingAmount, setRemainingAmount] = useState<number>(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [predefinedServices, setPredefinedServices] = useState<PredefinedService[]>([]);
  const [customServices, setCustomServices] = useState<CustomService[]>([]);
  
  // Payments state - now persisted in DB
  const [payments, setPayments] = useState<Payment[]>([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');
  const [currency, setCurrency] = useState<Currency>('USD');
  const [paymentAmount, setPaymentAmount] = useState<number | ''>('');
  
  // Add Services modal state
  const [showAddServicesModal, setShowAddServicesModal] = useState(false);
  const [selectedServicesToAdd, setSelectedServicesToAdd] = useState<string[]>([]);

  const [studentsDropdown, setStudentsDropdown] = useState<{ label: string; value: string; name: string }[]>([]);
  const studentsRef = useRef<Student[]>([]);
  const isEdit = Boolean(initialData && Object.keys(initialData).length > 0);

  
  React.useEffect(() => {
    if (opened) {
      if (isEdit && initialData) {
        setPackageId(initialData.packageId ?? '');
        setPackageType(initialData.packageType ?? null);
        setPackageGeneralStatus(initialData.packageGeneralStatus ?? 'Not Started');
        setBatchNumber(initialData.batchNumber ?? '');
        setStudentName(initialData.studentName ?? '');
        setStudentEmail(initialData.studentEmail ?? '');
        setTrainerEmail(initialData.trainerEmail ?? '');
        setDescription(initialData.remark ?? '');
        
        // Main package fields
        setCourseName(initialData.courseName ?? '');
        setPrice(initialData.price ?? 0);
        setReceivedAmount(initialData.receivedAmount ?? 0);
        setRemainingAmount(initialData.remainingAmount ?? 0);
        setCurrency((initialData.currency as Currency) ?? 'USD');  // ADD THIS LINE

        setStartDate(initialData.startDate ?? '');
        setEndDate(initialData.endDate ?? '');

        // Load payments from DB
        const paymentsFromDB = initialData.payments || [];
        setPayments(paymentsFromDB);

        // Extract predefined services
        const servicesObj = initialData.services || {};
        const predefined: PredefinedService[] = [];
        
        PREDEFINED_SERVICES.forEach(({ value }) => {
          if (servicesObj[value]) {
            const s = servicesObj[value];
            if (s.expectedDate || s.packageStatus !== 'Pending') {
              predefined.push({
                serviceKey: value,
                expectedDate: s.expectedDate ?? '',
                packageStatus: s.packageStatus ?? 'Pending',
              });
            }
          }
        });
        setPredefinedServices(predefined);

        // Extract custom services
        const customServicesObj = servicesObj.customServices || {};
        const custom: CustomService[] = Object.keys(customServicesObj).map((key) => {
          const s = customServicesObj[key];
          return {
            serviceName: s.serviceName ?? key,
            expectedDate: s.expectedDate ?? '',
            packageStatus: s.packageStatus ?? 'Pending',
          };
        });
        setCustomServices(custom);
      } else {
        // Reset for create mode
        setPackageId('');
        setPackageType(null);
        setPackageGeneralStatus('Not Started');
        setBatchNumber('');
        setStudentName('');
        setStudentEmail('');
        setTrainerEmail('');
        setDescription('');
        setCourseName('');
        setPrice(0);
        setReceivedAmount(0);
        setCurrency('USD');
        setRemainingAmount(0);
        setStartDate('');
        setEndDate('');
        setPredefinedServices([]);
        setCustomServices([]);
        setPayments([]);
      }
      setError(null);
      setPendingAttachments([]);
      setShowPaymentForm(false);
      setPaymentDate('');
      setPaymentAmount('');
    }
  }, [opened, initialData, isEdit]);

  // Auto-calculate remaining amount
  React.useEffect(() => {
    const remaining = (price || 0) - (receivedAmount || 0);
    setRemainingAmount(remaining);
  }, [price, receivedAmount]);

  const fetchStudents = useCallback(async () => {
    try {
      setStudentsLoading(true);
      const response = await ApiService.getInstance().get("/students/all-students");
      studentsRef.current = response.data;

      const formattedStudents = response.data.map(({ email, name }: Student) => ({
        label: `${name} — ${email}`,
        value: email,
        name: name,
      }));
      setStudentsDropdown(formattedStudents);
    } catch (error) {
      console.error("Error fetching students:", error);
      showNotification({
        title: "Error",
        message: "Failed to fetch students",
        color: "red",
      });
    } finally {
      setStudentsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (opened) {
      fetchStudents();
    }
  }, [opened, fetchStudents]);

  // Predefined Services handlers
  const addPredefinedService = () => {
    setPredefinedServices((s) => [
      ...s,
      {
        serviceKey: '',
        expectedDate: '',
        packageStatus: 'Pending',
      },
    ]);
  };

  const handleAddSelectedServices = () => {
    setError(null);
    if (!selectedServicesToAdd || selectedServicesToAdd.length === 0) {
      return setError('Please select at least one service to add');
    }

    setPredefinedServices((prev) => {
      const existing = new Set(prev.map((p) => p.serviceKey));
      const toAdd = selectedServicesToAdd.filter((v) => v && !existing.has(v));
      const newEntries = toAdd.map((v) => ({ serviceKey: v, expectedDate: '', packageStatus: 'Pending' }));
      return [...prev, ...newEntries];
    });

    setSelectedServicesToAdd([]);
    setShowAddServicesModal(false);
  };

  const removePredefinedService = (index: number) => {
    setPredefinedServices((s) => s.filter((_, i) => i !== index));
  };

  const updatePredefinedService = (index: number, patch: Partial<PredefinedService>) => {
    setPredefinedServices((s) => s.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  // Custom Services handlers
  const addCustomService = () => {
    setCustomServices((s) => [
      ...s,
      {
        serviceName: '',
        expectedDate: '',
        packageStatus: 'Pending',
      },
    ]);
  };

  const removeCustomService = (index: number) => {
    setCustomServices((s) => s.filter((_, i) => i !== index));
  };

  const updateCustomService = (index: number, patch: Partial<CustomService>) => {
    setCustomServices((s) => s.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  // Payment handlers
  const togglePaymentForm = () => {
    setShowPaymentForm((s) => !s);
    if (!showPaymentForm) {
      setPaymentDate('');
      setPaymentAmount('');
    }
  };

  const handleConfirmAddPayment = () => {
    setError(null);
    if (!paymentDate) return setError('Payment date is required');
    const amt = Number(paymentAmount) || 0;
    if (amt <= 0) return setError('Payment amount must be greater than zero');

    const newPayment: Payment = { 
      date: paymentDate, 
      amount: amt,
      addedAt: new Date().toISOString(),
      currency: currency,
    };
    setPayments((p) => [...p, newPayment]);

    // Update received / remaining amounts
    setReceivedAmount((r) => Number(r || 0) + amt);

    // Clear and hide
    setPaymentDate('');
    setPaymentAmount('');
    setShowPaymentForm(false);
  };

  const handleRemovePayment = (index: number) => {
    const toRemove = payments[index];
    if (!toRemove) return;
    setPayments((p) => p.filter((_, i) => i !== index));
    setReceivedAmount((r) => Number(r || 0) - Number(toRemove.amount || 0));
  };

  const getAvailablePredefinedServices = (currentIndex: number) => {
    const selectedKeys = predefinedServices
      .map((s, idx) => (idx !== currentIndex ? s.serviceKey : null))
      .filter(Boolean);
    
    return PREDEFINED_SERVICES.filter((s) => !selectedKeys.includes(s.value));
  };

  const handleSubmit = async () => {
    setError(null);

    if (isEdit && !packageId.trim()) return setError('Package ID is required');
    if (!packageType) return setError('Package Type is required');

    for (const svc of predefinedServices) {
      if (!svc.serviceKey) {
        return setError('Please select a service from dropdown for all predefined services');
      }
    }

    for (const svc of customServices) {
      if (!svc.serviceName?.trim()) {
        return setError('Please provide service name for all custom services');
      }
    }

    setLoading(true);

    try {
      const predefinedServicesData: Record<string, any> = {};
      predefinedServices.forEach((svc) => {
        predefinedServicesData[svc.serviceKey] = {
          expectedDate: svc.expectedDate || '',
          packageStatus: svc.packageStatus || 'Pending',
        };
      });

      const customServicesData = customServices.map((svc) => ({
        serviceName: svc.serviceName.trim(),
        expectedDate: svc.expectedDate || '',
        packageStatus: svc.packageStatus || 'Pending',
      }));

      const payload: any = {
        packageType,
        packageGeneralStatus,
        batchNumber: batchNumber.trim(),
        studentName: studentName.trim(),
        studentEmail: studentEmail.trim(),
        trainerEmail: trainerEmail.trim(),
        remark: description.trim(),
        courseName: courseName.trim(),
        price: Number(price) || 0,
        receivedAmount: Number(receivedAmount) || 0,
        remainingAmount: Number(remainingAmount) || 0,
        currency: currency,
        startDate: startDate || '',
        endDate: endDate || '',
        predefinedServices: predefinedServicesData,
        customServices: customServicesData,
        payments: payments, // Include payments in payload
      };

      // console.log(' Sending payload:', payload);

      if (isEdit) {
        await ApiService.getInstance().put(`/package/${packageId}`, payload);
        // console.log(' Updated package');
        showNotification({
          title: "Success",
          message: "Package updated successfully",
          color: "green",
        });
        onCreated?.();
        onClose();
      } else {
        const res = await ApiService.getInstance().post('/package/create', payload);
        const newId = res?.data?.packageId ?? res?.data?.id ?? null;
        if (newId) {
          setPackageId(String(newId));
          // console.log(' Created package with id', newId);
        } else {
          // console.log(' Created package but no id returned', res?.data);
        }
        showNotification({
          title: "Success",
          message: "Package created successfully",
          color: "green",
        });
        if (newId && pendingAttachments.length > 0) {
          try {
            await uploadEnrollmentAttachments(String(newId), pendingAttachments);
          } catch (uploadErr: any) {
            console.error(' Error uploading enrollment screenshots:', uploadErr);
            showNotification({
              title: "Upload failed",
              message: "Package was created, but the enrollment screenshots failed to upload. Open the package and upload them again.",
              color: "orange",
            });
          }
        }
        onCreated?.();
        onClose();
      }
    } catch (err: any) {
      console.error(' Error saving package:', err);
      setError(err?.message || 'Failed to save package');
      showNotification({
        title: "Error",
        message: err?.message || "Failed to save package",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Edit Package' : 'Create New Package'}
      centered
      size="lg"
    >
      <Stack>
        <Group grow>
          <TextInput
            label="Package ID"
            value={packageId}
            onChange={(e) => setPackageId(e.target.value)}
            placeholder={isEdit ? '' : 'Auto Generated'}
            required={isEdit}
            disabled={true}
            readOnly
          />
          <Select
            label="Package Type"
            placeholder="Select type"
            data={[
              { value: 'Job seekers', label: 'Job seekers' },
              { value: 'Bootcamp', label: 'Bootcamp' },
              { value: 'Others', label: 'Others' },
            ]}
            value={packageType}
            onChange={(val) => setPackageType(val)}
            required
          />
          <Select
  label="Package Status"
  placeholder="Select status"
  data={PACKAGE_GENERAL_STATUS_OPTIONS}
  value={packageGeneralStatus}
  onChange={(val) => setPackageGeneralStatus(val || 'Not Started')}
  required
/>
        </Group>
        

        <Group grow>
          <TextInput
            label="Batch Number"
            value={batchNumber}
            onChange={(e) => setBatchNumber(e.target.value)}
          />
          <TextInput
            label="Course Name"
            value={courseName}
            onChange={(e) => setCourseName(e.target.value)}
          />
        </Group>

        <Group grow>
          <Select
            label="Student Email"
            placeholder={studentsLoading ? 'Loading students...' : 'Select student by email'}
            searchable
            data={studentsDropdown.map((s) => ({ label: s.value, value: s.value }))}
            value={studentEmail}
            onChange={(val) => {
              const email = val ?? '';
              setStudentEmail(email);
              const found = studentsRef.current.find((st) => st.email === email);
              setStudentName(found?.name ?? '');
            }}
            disabled={studentsLoading}
            rightSection={studentsLoading ? <Loader size="xs" /> : undefined}
          />
          <Select
            label="Student (Name)"
            placeholder={studentsLoading ? 'Loading students...' : 'Select student by name'}
            searchable
            data={studentsDropdown.map((s) => ({ label: s.name, value: s.value }))}
            value={studentEmail}
            onChange={(val) => {
              const email = val ?? '';
              setStudentEmail(email);
              const found = studentsRef.current.find((st) => st.email === email);
              setStudentName(found?.name ?? '');
            }}
            disabled={studentsLoading}
            rightSection={studentsLoading ? <Loader size="xs" /> : undefined}
          />
        </Group>

        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          minRows={2}
        />

        <EnrollmentAttachments
          packageId={isEdit ? packageId : ''}
          pendingFiles={pendingAttachments}
          onPendingFilesChange={setPendingAttachments}
        />

        {/* Financial Details */}
        <Box style={{ padding: 16, border: '2px solid #228be6', borderRadius: 8, marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 16 }}>Financial Details{isEdit && batchNumber ? <span style={{ fontWeight: 400, fontSize: 12, color: '#228be6', marginLeft: 8 }}>(Synced from Batch #{batchNumber})</span> : null}</div>
          
          <Group grow style={{ marginBottom: 12 }}>
            <Select
    label="Currency"
    placeholder="Select currency"
    data={CURRENCY_OPTIONS}
    value={currency}
    onChange={(val) => setCurrency((val as Currency) || 'USD')}
    required
  />
            <NumberInput
              label="Total Amount"
              value={price}
              onChange={(val) => setPrice(val as number || 0)}
              min={0}
              step={0.01}
            
            />
          
          </Group>
      <Group grow style={{ marginBottom: 12 }}>
          <NumberInput
              label="Received Amount"
              value={receivedAmount}
              onChange={(val) => setReceivedAmount(val as number || 0)}
              min={0}
              step={0.01}
              // disabled
            />
            <NumberInput
              label="Remaining Amount"
              value={remainingAmount}
              onChange={(val) => setRemainingAmount(val as number || 0)}
              min={0}
              step={0.01}
              disabled
            />


      </Group>
          <Group grow>
            <TextInput
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              type="date"
            />
            <TextInput
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              type="date"
            />
          </Group>

          {/* Payments section */}
          <div style={{ marginTop: 16 }}>
            <Group justify="space-between" align="center" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>Payment History</div>
              <Button size="xs" leftSection={<IconPlus size={14} />} onClick={togglePaymentForm}>
                Add Payment
              </Button>
            </Group>

            {showPaymentForm && (
              <Box style={{ padding: 12, border: '1px solid #e6e6e6', borderRadius: 6, marginBottom: 12, backgroundColor: '#f9fafb' }}>
                <Group grow style={{ marginBottom: 8 }}>
                  <TextInput
                    label="Payment Date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    type="date"
                    required
                  />
                  <NumberInput
                    label="Payment Amount"
                    value={paymentAmount as number}
                    onChange={(val) => setPaymentAmount(val as number || '')}
                    min={0}
                    step={0.01}
                    // prefix="₹"
                    required
                  />
                </Group>

                <Group justify="flex-end">
                  <Button variant="default" size="xs" onClick={togglePaymentForm}>Cancel</Button>
                  <Button size="xs" onClick={handleConfirmAddPayment}>Add Payment</Button>
                </Group>
              </Box>
            )}

            {payments.length === 0 ? (
              <div style={{ color: '#6b7280', padding: '12px', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: 6 }}>
                No payments recorded yet.
              </div>
            ) : (
              <Box style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Amount</Table.Th>
                      <Table.Th>Added On</Table.Th>
                      <Table.Th style={{ width: 60 }}>Action</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {payments.map((p, idx) => (
                      <Table.Tr key={idx}>
                        <Table.Td>{p.date}</Table.Td>
                        <Table.Td style={{ fontWeight: 600 }}>{Number(p.amount).toFixed(2)}</Table.Td>
                        <Table.Td style={{ fontSize: 12, color: '#6b7280' }}>
                          {p.addedAt ? new Date(p.addedAt).toLocaleString() : '-'}
                        </Table.Td>
                        <Table.Td>
                          <ActionIcon color="red" variant="subtle" onClick={() => handleRemovePayment(idx)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            )}
          </div>
        </Box>

        <Tabs defaultValue="predefined" style={{ marginTop: 16 }}>
          <Tabs.List>
            <Tabs.Tab value="predefined">Predefined Services</Tabs.Tab>
            <Tabs.Tab value="custom">Custom Services</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="predefined" pt="md">
            <Box>
              <Group justify="space-between" style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 600 }}>Predefined Services</div>
                <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setShowAddServicesModal(true)}>
                  Add Service
                </Button>
              </Group>

              {predefinedServices.length === 0 && (
                <div style={{ color: '#6b7280', padding: '12px 0' }}>No predefined services added yet.</div>
              )}

              {predefinedServices.map((svc, idx) => (
                <Box
                  key={idx}
                  style={{
                    padding: 12,
                    border: '1px solid #e6e6e6',
                    borderRadius: 6,
                    marginBottom: 10,
                  }}
                >
                  <Group justify="space-between" align="start">
                    <div style={{ fontWeight: 600 }}>Service #{idx + 1}</div>
                    <ActionIcon color="red" onClick={() => removePredefinedService(idx)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>

                  <Group grow style={{ marginTop: 8 }}>
                    <Select
                      label="Service Name"
                      placeholder="Select service"
                      data={getAvailablePredefinedServices(idx)}
                      value={svc.serviceKey}
                      onChange={(val) => updatePredefinedService(idx, { serviceKey: val ?? '' })}
                    />
                    <TextInput
                      label="Expected Date"
                      value={svc.expectedDate}
                      onChange={(e) => updatePredefinedService(idx, { expectedDate: e.target.value })}
                      type="date"
                    />
                    <Select
                      label="Package Status"
                      placeholder="Select status"
                      data={PACKAGE_STATUS_OPTIONS}
                      value={svc.packageStatus}
                      onChange={(val) => updatePredefinedService(idx, { packageStatus: val ?? 'Pending' })}
                    />
                  </Group>
                </Box>
              ))}
            </Box>
          </Tabs.Panel>

          <Modal opened={showAddServicesModal} onClose={() => setShowAddServicesModal(false)} title="Add Services" centered>
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}>Select the services you want to add:</div>
              <Checkbox.Group value={selectedServicesToAdd} onChange={(vals) => setSelectedServicesToAdd(vals as string[])}>
                <Stack>
                  {PREDEFINED_SERVICES.map((s) => (
                    <Checkbox key={s.value} value={s.value} label={s.label} />
                  ))}
                </Stack>
              </Checkbox.Group>
            </div>

            <Group justify="flex-end">
              <Button variant="default" size="xs" onClick={() => { setShowAddServicesModal(false); setSelectedServicesToAdd([]); }}>Cancel</Button>
              <Button size="xs" onClick={handleAddSelectedServices}>Add Selected</Button>
            </Group>
          </Modal>

          <Tabs.Panel value="custom" pt="md">
            <Box>
              <Group justify="space-between" style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 600 }}>Custom Services</div>
                <Button size="xs" leftSection={<IconPlus size={14} />} onClick={addCustomService}>
                  Add Custom
                </Button>
              </Group>

              {customServices.length === 0 && (
                <div style={{ color: '#6b7280', padding: '12px 0' }}>No custom services added yet.</div>
              )}

              {customServices.map((svc, idx) => (
                <Box
                  key={idx}
                  style={{
                    padding: 12,
                    border: '1px solid #e6e6e6',
                    borderRadius: 6,
                    marginBottom: 10,
                  }}
                >
                  <Group justify="space-between" align="start">
                    <div style={{ fontWeight: 600 }}>{svc.serviceName || `Service #${idx + 1}`}</div>
                    <ActionIcon color="red" onClick={() => removeCustomService(idx)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>

                  <Group grow style={{ marginTop: 8 }}>
                    <TextInput
                      label="Service Name"
                      value={svc.serviceName}
                      onChange={(e) => updateCustomService(idx, { serviceName: e.target.value })}
                    />
                    <TextInput
                      label="Expected Date"
                      value={svc.expectedDate}
                      onChange={(e) => updateCustomService(idx, { expectedDate: e.target.value })}
                      type="date"
                    />
                    <Select
                      label="Package Status"
                      placeholder="Select status"
                      data={PACKAGE_STATUS_OPTIONS}
                      value={svc.packageStatus}
                      onChange={(val) => updateCustomService(idx, { packageStatus: val ?? 'Pending' })}
                    />
                  </Group>
                </Box>
              ))}
            </Box>
          </Tabs.Panel>
        </Tabs>

        {error && <div style={{ color: 'red', padding: '8px', backgroundColor: '#fee', borderRadius: 4 }}>{error}</div>}

        <Group justify="flex-end" style={{ marginTop: 16 }}>
          <Button variant="default" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={handleSubmit} loading={loading}>
            {isEdit ? 'Update Package' : 'Create Package'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}